import { getModel } from '../../model/registry'
import styled from 'styled-components'
import useGridListData from './useGridListData'
import useGridListActions from './useGridListActions'
import GridListTitle from './GridListTitle'
import GridListTable from './GridListTable'
import GridListTable_m from './GridListTable_m'
import ViewFilter from '../listView/ViewFilter'
import { ModalProvider } from '../modal/useModalDialog'
import { FlexBox } from '../generic/Layout'
import IconButton from '../generic/IconButton'
import { LinkText } from '../generic/Core'
import { Icon_Plus, Icon_Arrow_Right } from '../../styles/icons'
import useTranslation from '../../hooks/useTranslation'
import { useNavigate, useParams } from 'react-router-dom'
import usePath from '../../hooks/usePath'

const Spacer = styled.div`
    height: 1rem;
`

const AddLineButton = ({ label, onClick }) => {
    const { t } = useTranslation()

    if (!label) return null

    return (
        <>
            <FlexBox style={{ alignItems: 'center', marginTop: '0.5rem' }}>
                <IconButton icon={Icon_Plus} $rounded $variant='primary' $size='2rem' clickAction={onClick} />
                <LinkText onClick={onClick}>{t(label)}</LinkText>
            </FlexBox>
            <Spacer />
        </>
    )
}

const GridList = ({ type, options = {}, useStore, linesArray, storePath }) => {
    const config = getModel('gridListData')[type]
    const navigate = useNavigate()

    if (!config) {
        return <h2>Nothing to display, please define the GridList config for: "{type}"</h2>
    }

    const resolvedConfig = { ...config }
    if (linesArray) resolvedConfig.linesArray = linesArray
    if (storePath) resolvedConfig.storePath = storePath

    if (resolvedConfig.path) {
        const existingMenuItems = resolvedConfig.lineActions?.menuItems || []
        const openItem = { label: 'all.grid.open', action: 'open', icon: Icon_Arrow_Right }
        resolvedConfig.lineActions = {
            ...resolvedConfig.lineActions,
            menuItems: [openItem, ...existingMenuItems],
        }
    }

    if (!options) options = { viewFilters: true }

    const dataResult = useGridListData(resolvedConfig, options, useStore)
    const actions = useGridListActions(resolvedConfig, dataResult)

    const { rows, isLoading, isError, error, hasNextPage, fetchNextPage, mode } = dataResult
    const { effectiveRows, addItem, deleteItem, saveAll, fillAll, reorderItems, dirtyRows, savedRows, updateCellValue, AlertComponent } = actions

    const columns = resolvedConfig.columns || []
    const reorderable = resolvedConfig.reorderable || false
    const addLineLabel = resolvedConfig.addLine || false
    const entity = resolvedConfig.entity
    const tableKey = resolvedConfig.tableKey || 'id'
    const { url: openUrl } = usePath(resolvedConfig.path || '')

    const handleAction = (menuItem, item, rowIdx) => {
        if (menuItem.action === 'delete') {
            deleteItem(rowIdx)
        } else if (menuItem.action === 'open') {
            navigate(`${openUrl}${item[tableKey]}`)
        }
    }

    const handleUpdateCell = (rowIdx, field, value) => {
        updateCellValue(rowIdx, field, value)
    }

    const handleReorder = (oldIndex, newIndex) => {
        reorderItems(oldIndex, newIndex)
    }

    const handleSave = () => {
        saveAll()
    }

    const handleButtonAction = (btn) => {
        if (btn.action === 'fillFromField') {
            fillAll(btn.sourceField, btn.targetField)
        }
    }

    return (
        <ModalProvider>
            {resolvedConfig.showTitle !== false && (
                <GridListTitle
                    config={resolvedConfig}
                    dirtyRows={dirtyRows}
                    onSave={handleSave}
                    onAction={handleButtonAction}
                />
            )}
            {resolvedConfig.showFilterBar !== false && options.viewFilters && resolvedConfig.filters?.list?.length > 0 && (
                <ViewFilter
                    filters={resolvedConfig.filters}
                    filter={0}
                    setFilter={() => { }}
                    counts={{}}
                />
            )}
            <GridListTable
                rows={effectiveRows}
                columns={columns}
                config={resolvedConfig}
                isLoading={isLoading}
                isError={isError}
                error={error}
                onAction={handleAction}
                onUpdateCell={handleUpdateCell}
                useStore={useStore}
                entity={entity}
                reorderable={reorderable}
                onReorder={handleReorder}
                onAppend={resolvedConfig.tabAddsLine ? addItem : undefined}
                onAddFirst={addLineLabel && !resolvedConfig.readOnly ? addItem : undefined}
                dirtyRows={dirtyRows}
                savedRows={savedRows}
            />
            <GridListTable_m
                rows={effectiveRows}
                config={resolvedConfig}
                onAction={handleAction}
                onUpdateCell={handleUpdateCell}
                useStore={useStore}
                entity={entity}
                reorderable={reorderable}
                onReorder={handleReorder}
                dirtyRows={dirtyRows}
                savedRows={savedRows}
            />
            {addLineLabel && (
                <AddLineButton label={addLineLabel} onClick={addItem} />
            )}
            {hasNextPage && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                    <button onClick={() => fetchNextPage()}>Load More</button>
                </div>
            )}
            {AlertComponent}
        </ModalProvider>
    )
}

export default GridList
